import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useAuthor } from '@/hooks/useAuthor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/useToast';
import { 
  Upload, 
  Loader2, 
  Image as ImageIcon, 
  X,
  Music2,
  Disc3,
  Plus,
  GripVertical,
  Trash2,
  Sparkles,
  AlertCircle,
} from 'lucide-react';
import { getAudioDuration, isValidAudioFile, formatFileSize } from '@/lib/audioUtils';
import { cn } from '@/lib/utils';
import type { NostrMetadata } from '@nostrify/nostrify';

const GENRES = [
  'electronic', 'ambient', 'rock', 'jazz', 'classical', 'hiphop',
  'pop', 'indie', 'metal', 'folk', 'blues', 'reggae',
  'country', 'rnb', 'soul', 'funk', 'house', 'techno',
  'trance', 'dubstep', 'dnb', 'experimental', 'lofi', 'chillout'
];

interface TrackFile {
  id: string;
  file: File;
  title: string;
  duration: number;
  coverFile?: File;
  coverPreview?: string;
}

interface UploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UploadDialog({ open, onOpenChange }: UploadDialogProps) {
  const { user } = useCurrentUser();
  const author = useAuthor(user?.pubkey);
  const metadata: NostrMetadata | undefined = author.data?.metadata;
  const { mutateAsync: uploadFile, isPending: isUploading } = useUploadFile();
  const { mutateAsync: createEvent, isPending: isPublishing } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [uploadType, setUploadType] = useState<'single' | 'album'>('single');
  const [tracks, setTracks] = useState<TrackFile[]>([]);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [albumName, setAlbumName] = useState('');
  const [albumDescription, setAlbumDescription] = useState('');
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [ownershipConfirmed, setOwnershipConfirmed] = useState(false);
  const [isAiGenerated, setIsAiGenerated] = useState(false);

  const isLoading = isUploading || isPublishing || isSubmitting;

  const resetForm = () => {
    setUploadType('single');
    setTracks([]);
    setCoverFile(null);
    setCoverPreview('');
    setAlbumName('');
    setAlbumDescription('');
    setSelectedGenres([]);
    setUploadProgress(0);
    setOwnershipConfirmed(false);
    setIsAiGenerated(false);
  };

  const handleClose = () => {
    if (!isLoading) {
      resetForm();
      onOpenChange(false);
    }
  };

  const handleAudioFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(isValidAudioFile);
    
    if (validFiles.length === 0) {
      toast({
        title: 'Invalid files',
        description: 'Please upload valid audio files (MP3, WAV, OGG, FLAC)',
        variant: 'destructive',
      });
      return;
    }

    const newTracks: TrackFile[] = [];
    
    for (const file of validFiles) {
      const duration = await getAudioDuration(file);
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      
      newTracks.push({
        id: crypto.randomUUID(),
        file,
        title: nameWithoutExt,
        duration,
      });
    }

    if (uploadType === 'single') {
      setTracks([newTracks[0]]);
    } else {
      setTracks(prev => [...prev, ...newTracks]);
    }
  }, [uploadType, toast]);

  const handleCoverFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file',
        description: 'Please upload an image file',
        variant: 'destructive',
      });
      return;
    }

    setCoverFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setCoverPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const removeTrack = (id: string) => {
    setTracks(prev => prev.filter(t => t.id !== id));
  };

  const updateTrackTitle = (id: string, title: string) => {
    setTracks(prev => prev.map(t => t.id === id ? { ...t, title } : t));
  };

  const updateTrackCover = (id: string, file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setTracks(prev => prev.map(t => 
        t.id === id ? { ...t, coverFile: file, coverPreview: reader.result as string } : t
      ));
    };
    reader.readAsDataURL(file);
  };

  const removeTrackCover = (id: string) => {
    setTracks(prev => prev.map(t => 
      t.id === id ? { ...t, coverFile: undefined, coverPreview: undefined } : t
    ));
  };

  const toggleGenre = (genre: string) => {
    setSelectedGenres(prev => 
      prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]
    );
  };

  // For albums: require album name, album cover, and each track must have a title and cover
  // For singles: just require the track and genres
  const allTracksValid = tracks.every(t => t.title.trim().length > 0);
  const allTracksHaveCover = uploadType === 'single' || tracks.every(t => t.coverFile || coverFile);
  
  const canSubmit = tracks.length > 0 && 
    selectedGenres.length > 0 && 
    ownershipConfirmed &&
    allTracksValid &&
    (uploadType === 'single' || (albumName.trim() && coverFile && allTracksHaveCover));

  const handleSubmit = async () => {
    if (!user || !canSubmit) return;

    setIsSubmitting(true);
    setUploadProgress(5);

    try {
      const artistName = metadata?.display_name || metadata?.name || 'Unknown Artist';
      
      // Upload cover if provided
      let coverUrl: string | undefined;
      if (coverFile) {
        const coverTags = await uploadFile(coverFile);
        coverUrl = coverTags[0][1];
      }
      setUploadProgress(20);

      const totalTracks = tracks.length;
      let completed = 0;

      // Upload each track
      for (const track of tracks) {
        const audioTags = await uploadFile(track.file);
        const audioUrl = audioTags[0][1];
        const audioHash = audioTags.find(([name]) => name === 'x')?.[1];

        if (!audioUrl || !audioHash) {
          throw new Error(`Failed to upload: ${track.title}`);
        }

        // Upload individual track cover if provided, otherwise use album cover
        let trackCoverUrl = coverUrl;
        if (track.coverFile) {
          const trackCoverTags = await uploadFile(track.coverFile);
          trackCoverUrl = trackCoverTags[0][1];
        }

        // Create d-tag
        const dTag = track.title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '') + '-' + Date.now().toString(36);

        const tags: string[][] = [
          ['d', dTag],
          ['title', track.title],
          ['artist', artistName],
          ['url', audioUrl],
          ['x', audioHash],
          ['m', track.file.type || 'audio/mpeg'],
          ['size', track.file.size.toString()],
          ['published_at', Math.floor(Date.now() / 1000).toString()],
          ['alt', `Music track: ${track.title} by ${artistName}`],
        ];

        // Always add duration (even if 0, player will get real duration)
        tags.push(['duration', (track.duration || 0).toString()]);

        // Add album if this is an album upload
        if (uploadType === 'album' && albumName.trim()) {
          tags.push(['album', albumName.trim()]);
        }

        // Add genres
        selectedGenres.forEach(genre => {
          tags.push(['t', genre.toLowerCase()]);
        });

        // Add cover (track-specific or album cover)
        if (trackCoverUrl) {
          tags.push(['image', trackCoverUrl]);
        }

        // Add AI-generated tag if checked
        if (isAiGenerated) {
          tags.push(['ai', 'true']);
        }

        await createEvent({
          kind: 30999,
          content: uploadType === 'album' ? albumDescription : '',
          tags,
        });

        completed++;
        setUploadProgress(20 + (completed / totalTracks) * 75);
      }

      setUploadProgress(100);

      toast({
        title: 'Upload complete!',
        description: `${tracks.length} track${tracks.length > 1 ? 's' : ''} published successfully`,
      });

      queryClient.invalidateQueries({ queryKey: ['music-tracks'] });
      queryClient.invalidateQueries({ queryKey: ['artist-tracks'] });

      handleClose();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Something went wrong',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  if (!user) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <div className="py-12 text-center">
            <Disc3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Please sign in to upload music</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Upload Music
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Upload Type Tabs */}
          <Tabs value={uploadType} onValueChange={(v) => {
            setUploadType(v as 'single' | 'album');
            setTracks([]);
          }}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="single" className="gap-2">
                <Music2 className="h-4 w-4" />
                Single Track
              </TabsTrigger>
              <TabsTrigger value="album" className="gap-2">
                <Disc3 className="h-4 w-4" />
                Album / EP
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Album Details */}
          {uploadType === 'album' && (
            <div className="space-y-4 p-4 rounded-lg bg-muted/50">
              <div className="space-y-2">
                <Label htmlFor="albumName">Album Name *</Label>
                <Input
                  id="albumName"
                  value={albumName}
                  onChange={(e) => setAlbumName(e.target.value)}
                  placeholder="Enter album name"
                  className={cn(!albumName.trim() && 'border-amber-500/50')}
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="albumDesc">Description</Label>
                <Textarea
                  id="albumDesc"
                  value={albumDescription}
                  onChange={(e) => setAlbumDescription(e.target.value)}
                  placeholder="About this album..."
                  rows={2}
                  disabled={isLoading}
                />
              </div>
            </div>
          )}

          {/* Audio Files */}
          <div className="space-y-3">
            <Label>Audio Files *</Label>
            
            {tracks.length > 0 && (
              <div className="space-y-3">
                {tracks.map((track, index) => (
                  <motion.div
                    key={track.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-start gap-3 p-3 rounded-lg bg-muted/50"
                  >
                    <div className="flex items-center gap-2 pt-2">
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground w-6">{index + 1}</span>
                    </div>
                    
                    {/* Track cover for album mode */}
                    {uploadType === 'album' && (
                      <div className="flex-shrink-0">
                        {track.coverPreview ? (
                          <div className="relative w-14 h-14">
                            <img
                              src={track.coverPreview}
                              alt="Track cover"
                              className="w-full h-full object-cover rounded-lg"
                            />
                            <button
                              onClick={() => removeTrackCover(track.id)}
                              disabled={isLoading}
                              className="absolute -top-1 -right-1 h-5 w-5 bg-destructive rounded-full flex items-center justify-center"
                            >
                              <X className="h-3 w-3 text-white" />
                            </button>
                          </div>
                        ) : (
                          <label className="block cursor-pointer">
                            <div className="w-14 h-14 border-2 border-dashed rounded-lg flex flex-col items-center justify-center hover:border-primary/50 transition-colors">
                              <ImageIcon className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <Input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file && file.type.startsWith('image/')) {
                                  updateTrackCover(track.id, file);
                                }
                              }}
                              disabled={isLoading}
                              className="hidden"
                            />
                          </label>
                        )}
                      </div>
                    )}
                    
                    <div className="flex-1 space-y-1">
                      <Input
                        value={track.title}
                        onChange={(e) => updateTrackTitle(track.id, e.target.value)}
                        placeholder="Song title *"
                        className={cn(
                          !track.title.trim() && 'border-amber-500/50'
                        )}
                        disabled={isLoading}
                      />
                      <span className="text-xs text-muted-foreground">
                        {formatFileSize(track.file.size)}
                      </span>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeTrack(track.id)}
                      disabled={isLoading}
                      className="h-8 w-8 mt-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </motion.div>
                ))}
              </div>
            )}

            <label className="block">
              <div className={cn(
                'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors',
                'hover:border-primary/50 hover:bg-primary/5',
                isLoading && 'opacity-50 pointer-events-none'
              )}>
                <Plus className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {uploadType === 'single' ? 'Click to select audio file' : 'Click to add tracks'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  MP3, WAV, OGG, FLAC
                </p>
              </div>
              <Input
                type="file"
                accept="audio/*"
                multiple={uploadType === 'album'}
                onChange={(e) => e.target.files && handleAudioFiles(e.target.files)}
                disabled={isLoading}
                className="hidden"
              />
            </label>
          </div>

          {/* Cover Art */}
          <div className="space-y-3">
            <Label>
              {uploadType === 'album' ? 'Album Cover *' : 'Cover Art'}
              {uploadType === 'album' && (
                <span className="text-xs text-muted-foreground ml-2">(used as default for all tracks)</span>
              )}
            </Label>
            {coverPreview ? (
              <div className="relative w-32 h-32">
                <img
                  src={coverPreview}
                  alt="Cover"
                  className="w-full h-full object-cover rounded-lg"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute -top-2 -right-2 h-6 w-6"
                  onClick={() => {
                    setCoverFile(null);
                    setCoverPreview('');
                  }}
                  disabled={isLoading}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <label className="block w-32">
                <div className={cn(
                  'w-32 h-32 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors',
                  uploadType === 'album' && !coverFile && 'border-amber-500/50'
                )}>
                  <ImageIcon className="h-6 w-6 text-muted-foreground mb-1" />
                  <span className="text-xs text-muted-foreground">Add cover</span>
                </div>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverFile}
                  disabled={isLoading}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Genres */}
          <div className="space-y-3">
            <Label>Genres * (select at least one)</Label>
            <div className="flex flex-wrap gap-2">
              {GENRES.map((genre) => (
                <Badge
                  key={genre}
                  variant={selectedGenres.includes(genre) ? 'default' : 'outline'}
                  className={cn(
                    'cursor-pointer capitalize transition-all',
                    selectedGenres.includes(genre) ? '' : 'hover:bg-primary/10'
                  )}
                  onClick={() => !isLoading && toggleGenre(genre)}
                >
                  {genre}
                </Badge>
              ))}
            </div>
          </div>

          {/* AI Generated Checkbox */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20">
            <Checkbox
              id="ai-generated"
              checked={isAiGenerated}
              onCheckedChange={(checked) => setIsAiGenerated(checked === true)}
              disabled={isLoading}
              className="mt-0.5"
            />
            <div className="flex-1">
              <label htmlFor="ai-generated" className="text-sm font-medium flex items-center gap-2 cursor-pointer">
                <Sparkles className="h-4 w-4 text-purple-400" />
                This music was created with AI
              </label>
              <p className="text-xs text-muted-foreground mt-1">
                Check this if the music was generated or significantly assisted by AI tools
              </p>
            </div>
          </div>

          {/* Ownership Confirmation */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <Checkbox
              id="ownership"
              checked={ownershipConfirmed}
              onCheckedChange={(checked) => setOwnershipConfirmed(checked === true)}
              disabled={isLoading}
              className="mt-0.5"
            />
            <div className="flex-1">
              <label htmlFor="ownership" className="text-sm font-medium flex items-center gap-2 cursor-pointer">
                <AlertCircle className="h-4 w-4 text-amber-400" />
                I confirm ownership or permission *
              </label>
              <p className="text-xs text-muted-foreground mt-1">
                I own the rights to this music or have explicit permission to upload and distribute it
              </p>
            </div>
          </div>

          {/* Progress */}
          {isSubmitting && (
            <div className="space-y-2">
              <Progress value={uploadProgress} />
              <p className="text-sm text-center text-muted-foreground">
                Uploading... {Math.round(uploadProgress)}%
              </p>
            </div>
          )}

          {/* Validation Messages for Album */}
          {uploadType === 'album' && tracks.length > 0 && !canSubmit && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm">
              <p className="font-medium text-amber-400 flex items-center gap-2 mb-2">
                <AlertCircle className="h-4 w-4" />
                Please complete the following:
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 ml-6 list-disc">
                {!albumName.trim() && <li>Add an album name</li>}
                {!coverFile && <li>Add an album cover image</li>}
                {!allTracksValid && <li>Add a title for each song</li>}
                {selectedGenres.length === 0 && <li>Select at least one genre</li>}
                {!ownershipConfirmed && <li>Confirm ownership/permission</li>}
              </ul>
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit || isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Publish {tracks.length > 1 ? `${tracks.length} Tracks` : 'Track'}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
